import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { useAuth } from "./authentication/AuthContext";
import { formatIndianCurrency } from "../utils/Const";
import { 
  Clock, 
  Trash2, 
  Printer, 
  CreditCard, 
  Plus, 
  Search, 
  ShoppingBag, 
  RefreshCw, 
  Utensils
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const OngoingOrders = () => {
  // Same rule as the server: only cashiers, managers and owners cancel orders
  const { isStaff, staffRole } = useAuth();
  const canCancelOrders = !isStaff || ["cashier", "manager"].includes(staffRole);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL"); // "ALL" | "DINE_IN" | "DIRECT"
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOngoingOrders();
  }, []);

  const fetchOngoingOrders = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await api.get("/orders/ongoing");
      setOrders(res.data || []);
    } catch (err) {
      console.error("Failed to fetch ongoing orders:", err);
      toast.error("Failed to load ongoing orders.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to cancel this order? This will restore any reserved stock.")) return;

    try {
      setActionLoading(orderId);
      await api.post(`/orders/${orderId}/cancel`, { reason: "POS Ongoing Cancel" });
      toast.success("Order cancelled successfully!");
      fetchOngoingOrders(true);
    } catch (err) {
      console.error("Cancel order failed:", err);
      toast.error(err.response?.data?.message || "Failed to cancel order.");
    } finally {
      setActionLoading(null);
    }
  };

  const getRelativeTime = (timestamp) => {
    if (!timestamp) return "just now";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ${diffMins % 60}m ago`;
    return `${diffDays}d ago`;
  };

  const [kotConfirm, setKotConfirm] = useState(null); // { order, kotRound }

  const doPrintKOT = (order, itemsToPrint, kotRound, isReprint = false) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const isAddOn = kotRound > 1 && !isReprint;

    printWindow.document.write(`
      <html>
        <head>
          <title>KOT #${order.orderNumber} — Round ${kotRound}</title>
          <style>
            * { margin:0; padding:0; box-sizing:border-box; }
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              font-size: 13px;
              padding: 8px 10px;
              width: 100%;
            }
            .header { text-align:center; border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 8px; }
            .kot-title { font-size: 17px; font-weight: 900; text-transform: uppercase; letter-spacing: 1px; }
            .addon-banner {
              background: #000; color: #fff;
              text-align: center; font-weight: 900;
              font-size: 13px; padding: 4px 6px;
              margin: 6px 0; letter-spacing: 1px;
            }
            .meta { font-size: 12px; line-height: 1.6; border-bottom: 1px dashed #000; padding-bottom: 6px; margin-bottom: 6px; }
            .meta-row { display:flex; justify-content:space-between; }
            table { width:100%; border-collapse:collapse; }
            th { font-weight:900; font-size:12px; padding: 3px 0; border-bottom: 1px solid #000; text-align:left; }
            th.qty { text-align:right; width:30px; }
            td { padding: 5px 0 3px; font-weight:700; vertical-align:top; }
            td.qty { text-align:right; font-weight:900; font-size:16px; }
            .sub { font-size:11px; color:#333; font-style:italic; font-weight:500; }
            .footer { text-align:center; border-top:1px dashed #000; margin-top:12px; padding-top:6px; font-size:10px; }
            .footer-space { height: 40px; }
            @page { margin: 2mm 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="kot-title">Kitchen Order Ticket</div>
            <div style="font-size:11px;font-weight:700;margin-top:3px;">
              ${isReprint ? "🔁 REPRINT" : `Round ${kotRound}`} &nbsp;|&nbsp;
              Status: ${order.status === "OPEN" ? "NEW" : order.status}
            </div>
          </div>

          ${isAddOn ? `<div class="addon-banner">⚡ ADD-ON ORDER ⚡</div>` : ""}

          <div class="meta">
            <div class="meta-row">
              <span><strong>KOT No:</strong> #${order.orderNumber}</span>
              <span><strong>Type:</strong> ${order.orderType === "DINE_IN" ? "Dine-In" : "Takeaway"}</span>
            </div>
            ${order.orderType === "DINE_IN" ? `
            <div class="meta-row" style="margin-top:2px;">
              <span><strong>Table:</strong> ${order.unitNumber}</span>
              <span><strong>Floor:</strong> ${order.floorName} / ${order.sectionName}</span>
            </div>` : `
            <div class="meta-row" style="margin-top:2px;">
              <span><strong>Token:</strong> ${order.parcelToken || "P" + order.orderNumber}</span>
            </div>`}
            <div style="margin-top:2px;">
              <strong>Time:</strong> ${new Date().toLocaleString("en-IN")}
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th class="qty">Qty</th>
              </tr>
            </thead>
            <tbody>
              ${itemsToPrint.map(item => `
                <tr>
                  <td>
                    <div>${item.name}</div>
                    ${item.variant ? `<div class="sub">↳ ${item.variant.name || item.variant}</div>` : ""}
                    ${item.addOns && item.addOns.length > 0 ? `<div class="sub">+ ${item.addOns.map(a => a.name).join(", ")}</div>` : ""}
                  </td>
                  <td class="qty">${item.qty}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>

          ${order.notes ? `<div style="margin-top:8px;border-top:1px dashed #000;padding-top:5px;font-size:12px;"><strong>Note:</strong> ${order.notes}</div>` : ""}

          <div class="footer">CloudRasoi Billing System</div>
          <div class="footer-space"></div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  const handlePrintKOT = async (order, forceReprint = false) => {
    try {
      // Sync any pending items first
      const res = await api.post(`/orders/${order._id}/kot-print`, { forceReprint });
      const { alreadyPrinted, kotRound, itemsToPrint, order: updatedOrder } = res.data;

      if (alreadyPrinted && !forceReprint) {
        // Show confirmation popup — all items already printed
        setKotConfirm({ order: updatedOrder || order, kotRound });
        return;
      }

      doPrintKOT(updatedOrder || order, itemsToPrint, kotRound, forceReprint);
      toast.success(
        forceReprint
          ? `KOT Reprint: Order #${order.orderNumber}`
          : `KOT Round ${kotRound} sent to kitchen for Order #${order.orderNumber}`
      );
      fetchOngoingOrders(true);
    } catch (err) {
      console.error("KOT print failed:", err);
      toast.error(err.response?.data?.message || "KOT print failed");
    }
  };


  // Filtered orders
  const filteredOrders = orders.filter((order) => {
    const matchesSearch = 
      String(order.orderNumber).includes(search) ||
      (order.floorName && order.floorName.toLowerCase().includes(search.toLowerCase())) ||
      (order.sectionName && order.sectionName.toLowerCase().includes(search.toLowerCase())) ||
      (order.unitNumber && String(order.unitNumber).includes(search)) ||
      (order.customer?.name && order.customer.name.toLowerCase().includes(search.toLowerCase())) ||
      (order.customer?.mobile && order.customer.mobile.includes(search));

    const matchesType = 
      typeFilter === "ALL" || 
      order.orderType === typeFilter;

    return matchesSearch && matchesType;
  });

  return (
    <div className="min-h-screen bg-slate-50/50 pt-20 pb-12 px-4 md:px-8">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ✅ KOT Already-Printed Confirmation Modal */}
      {kotConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl p-7 max-w-sm w-full mx-4 text-center">
            <div className="text-5xl mb-3">🍳</div>
            <h2 className="text-xl font-black text-slate-800 mb-1">Already Sent to Kitchen!</h2>
            <p className="text-sm text-slate-500 font-medium mb-1">
              Order <strong>#{kotConfirm.order.orderNumber}</strong> — all items have already been KOT printed.
            </p>
            <p className="text-xs text-slate-400 font-semibold mb-5">
              KOT Round {kotConfirm.kotRound} was the last print. No new items added yet.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  handlePrintKOT(kotConfirm.order, true);
                  setKotConfirm(null);
                }}
                className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-sm rounded-2xl flex items-center justify-center gap-2 transition shadow-sm"
              >
                <Printer className="w-4 h-4" /> Yes, Reprint All Items
              </button>
              <button
                onClick={() => setKotConfirm(null)}
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-extrabold text-sm rounded-2xl transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200/60 pb-5">
          <div>
            <h1 className="text-3xl font-black text-slate-800 tracking-tight flex items-center gap-2">
              Ongoing Orders
              <span className="text-sm bg-teal-50 text-teal-600 px-3 py-1 rounded-full border border-teal-200 font-bold">
                {orders.length} Active
              </span>
            </h1>
            <p className="text-sm text-slate-500 font-medium mt-1">
              Manage live/pending tables and parcels before bill generation and settlement.
            </p>
          </div>

          <button
            onClick={() => fetchOngoingOrders()}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-slate-600 hover:text-slate-900 transition shadow-sm hover:shadow-md"
          >
            <RefreshCw className="w-4 h-4" /> Refresh Status
          </button>
        </div>

        {/* Filters and Controls */}
        <div className="bg-white rounded-3xl p-5 border border-slate-200/65 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-1/3">
            <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search table, order #, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-teal-500 focus:outline-none text-sm font-semibold transition"
            />
          </div>

          <div className="flex gap-2 w-full md:w-auto">
            {["ALL", "DINE_IN", "DIRECT"].map((type) => (
              <button
                key={type}
                onClick={() => setTypeFilter(type)}
                className={`flex-1 md:flex-none px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all border ${
                  typeFilter === type
                    ? "bg-slate-900 border-slate-900 text-white shadow-sm"
                    : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                }`}
              >
                {type === "ALL" ? "All Orders" : type === "DINE_IN" ? "Dine-In 🪑" : "Takeaway 🛍"}
              </button>
            ))}
          </div>
        </div>

        {/* Orders Listing */}
        {loading ? (
          <div className="flex flex-col items-center justify-center min-h-[40vh] bg-white rounded-3xl border border-slate-200/60 shadow-sm">
            <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-xs font-bold text-slate-400 uppercase tracking-widest animate-pulse">
              Syncing Live Orders...
            </p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-16 text-center">
            <span className="text-5xl block mb-4">🍽️</span>
            <h3 className="text-xl font-bold text-slate-800">No ongoing orders</h3>
            <p className="text-xs text-slate-400 mt-1.5 max-w-xs mx-auto">
              There are currently no active tables or open takeaway carts to bill.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredOrders.map((order) => {
              const itemsCount = order.items?.reduce((sum, i) => sum + i.qty, 0) || 0;
              return (
                <div 
                  key={order._id} 
                  className="bg-white rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition duration-300 flex flex-col justify-between overflow-hidden"
                >
                  {/* Card Top / Title */}
                  <div className="p-6 pb-4 border-b border-slate-100 flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-base font-black text-slate-800">#{order.orderNumber}</span>
                        {order.source === "QR" && (
                          <span className="bg-teal-50 border border-teal-200 text-teal-700 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider flex items-center gap-0.5 animate-pulse">
                            QR Order
                          </span>
                        )}
                      </div>
                      <p 
                        className="text-xs text-slate-400 font-semibold mt-1 flex items-center gap-1 cursor-help"
                        title={new Date(order.createdAt || Date.now()).toLocaleString()}
                      >
                        <Clock className="w-3.5 h-3.5" /> 
                        {getRelativeTime(order.createdAt)}
                      </p>
                    </div>

                    <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                      order.status === "OPEN" 
                        ? "bg-sky-50 text-sky-700 border border-sky-200"
                        : order.status === "PREPARING"
                          ? "bg-amber-50 text-amber-700 border border-amber-200 animate-pulse"
                          : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`}>
                      {order.status}
                    </div>
                  </div>

                  {/* Card Details / Info */}
                  <div className="p-6 py-4 flex-1 space-y-4">
                    {/* Location Badge (Dine In) / Parcel Token */}
                    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3 flex items-center justify-between text-sm font-bold text-slate-700">
                      {order.orderType === "DINE_IN" ? (
                        <>
                          <span className="flex items-center gap-1.5">
                            <Utensils className="w-4 h-4 text-slate-400" />
                            {order.sectionType === "TABLE" ? "Table" : "Room"} {order.unitNumber}
                          </span>
                          <span className="text-xs font-semibold text-slate-450 uppercase">{order.floorName} / {order.sectionName}</span>
                        </>
                      ) : (
                        <>
                          <span className="flex items-center gap-1.5">
                            <ShoppingBag className="w-4 h-4 text-slate-400" />
                            Takeaway
                          </span>
                          <span className="font-mono text-teal-600 font-extrabold">{order.parcelToken || `P${order.orderNumber}`}</span>
                        </>
                      )}
                    </div>

                    {/* Customer Info if any */}
                    {(order.customer?.name || order.customer?.mobile) && (
                      <div className="text-xs space-y-0.5">
                        <p className="text-slate-500 font-semibold">Customer Details:</p>
                        <p className="text-slate-800 font-bold">{order.customer.name || "Guest"} - {order.customer.mobile || "NA"}</p>
                      </div>
                    )}

                    {/* Ordered Items Preview */}
                    <div className="space-y-2">
                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Order Items ({itemsCount}):</p>
                      <div className="max-h-[100px] overflow-y-auto space-y-1.5 scrollbar-thin">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center text-xs font-bold text-slate-700">
                            <span className="truncate max-w-[170px]">{item.qty} × {item.name}</span>
                            <span className="font-mono text-slate-500">₹{formatIndianCurrency(item.price * item.qty)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Card Bottom / Actions */}
                  <div className="p-6 pt-4 border-t border-slate-50 bg-slate-50/50 flex flex-col gap-3">
                    <div className="flex justify-between items-center font-bold text-slate-800 text-sm">
                      <span>Subtotal Estimation:</span>
                      <span className="text-lg font-black text-slate-900 font-mono">₹{formatIndianCurrency(order.totalAmount)}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Print KOT */}
                      <button
                        onClick={() => handlePrintKOT(order)}
                        className={`py-2.5 border font-extrabold text-xs rounded-xl flex items-center justify-center gap-1 transition ${
                          order.kotRound > 0
                            ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                        }`}
                      >
                        <Printer className="w-3.5 h-3.5" />
                        {order.kotRound > 0 ? `KOT R${order.kotRound}` : "Print KOT"}
                      </button>

                      {/* Modify Order */}
                      <button
                        onClick={() => navigate(`/home?orderId=${order._id}`)}
                        className="py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-extrabold text-xs rounded-xl flex items-center justify-center gap-1 transition shadow-sm"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add Items
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Cancel Order */}
                      {canCancelOrders && (
                      <button
                        onClick={() => handleCancelOrder(order._id)}
                        disabled={actionLoading === order._id}
                        className="py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-100 font-extrabold text-xs rounded-xl flex items-center justify-center gap-1 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Cancel
                      </button>
                      )}

                      {/* Settle/Close Order (Generate Bill) */}
                      <button
                        onClick={() => navigate(`/home?orderId=${order._id}`)}
                        className="py-2.5 bg-slate-900 hover:bg-black text-white font-extrabold text-xs rounded-xl flex items-center justify-center gap-1 transition shadow-md"
                      >
                        <CreditCard className="w-3.5 h-3.5" /> Generate Bill
                      </button>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
};

export default OngoingOrders;
